import { Injectable, Injector } from '@angular/core';
import {
  Dictionary,
  IndexedDbResourceLocation,
  interpolate,
  SOFTLINE_CONFIG_INDEXED_DB,
} from '@softline/core';

@Injectable({ providedIn: 'root' })
export class IndexedDbDataAccess {
  private databases: IDBDatabase[] = [];

  constructor(private injector: Injector) {}

  async get<T>(location: IndexedDbResourceLocation & { key: string }): Promise<T> {
    const database = await this.getDatabase(location.databaseName, location.params);
    const transaction = database.transaction([location.objectStoreName], 'readonly');
    const store = transaction.objectStore(location.objectStoreName);

    return await new Promise((resolve, reject) => {
      const request = store.get(location.key);
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        console.log(
          `Cannot get resource ${location.key} in ${location.databaseName}/${location.objectStoreName}`
        );
        reject(request.error);
      };
    });
  }

  async getAll<T>(location: IndexedDbResourceLocation): Promise<T[]> {
    const database = await this.getDatabase(location.databaseName, location.params);
    const transaction = database.transaction([location.objectStoreName], 'readonly');
    const store = transaction.objectStore(location.objectStoreName);

    return await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        console.log(
          `Cannot get resource ${location.key} in ${location.databaseName}/${location.objectStoreName}`
        );
        reject(request.error);
      };
    });
  }

  async add<T>(location: IndexedDbResourceLocation, resource: T): Promise<T> {
    const database = await this.getDatabase(location.databaseName, location.params);
    const transaction = database.transaction([location.objectStoreName], 'readwrite');
    const store = transaction.objectStore(location.objectStoreName);
    return await new Promise((resolve, reject) => {
      const isInlineKey = (store.keyPath?.length ?? 0) > 0;
      const request = store.add(resource, isInlineKey ? undefined : location.key);
      request.onsuccess = (event) => {
        const copy = this.createCopyWithKey(
          resource,
          location.key ?? (event.target as any).result,
          store.keyPath ?? []
        );
        resolve(copy as any);
      };
      request.onerror = () => {
        console.log(
          `Cannot add resource ${location.key} in ${location.databaseName}/${location.objectStoreName}`
        );
        reject(request.error);
      };
    });
  }

  async delete<T>(location: IndexedDbResourceLocation & { key: string }): Promise<T> {
    const deleted = await this.get<T>(location);

    const database = await this.getDatabase(location.databaseName, location.params);
    const transaction = database.transaction([location.objectStoreName], 'readwrite');
    const store = transaction.objectStore(location.objectStoreName);
    return await new Promise((resolve, reject) => {
      try {
        const request = store.delete(location.key);
        request.onsuccess = (event) => {
          resolve(deleted);
        };
        request.onerror = () => {
          console.log(
            `Cannot delete resource ${location.key} in ${location.databaseName}/${location.objectStoreName}`
          );
          reject(request.error);
        };
      } catch (e) {
        console.log(e);
        throw e;
      }
    });
  }

  async clear<T>(location: IndexedDbResourceLocation): Promise<T[]> {
    const deleted = await this.getAll<T>(location);

    const database = await this.getDatabase(location.databaseName, location.params);
    const transaction = database.transaction([location.objectStoreName], 'readwrite');
    const store = transaction.objectStore(location.objectStoreName);
    return await new Promise((resolve, reject) => {
      try {
        const request = store.clear();
        request.onsuccess = (event) => {
          resolve(deleted);
        };
        request.onerror = () => {
          console.log(
            `Cannot delete resource ${location.key} in ${location.databaseName}/${location.objectStoreName}`
          );
          reject(request.error);
        };
      } catch (e) {
        console.log(e);
        throw e;
      }
    });
  }

  async put<T, TResponse>(
    location: IndexedDbResourceLocation & { key: string },
    resource: T
  ): Promise<TResponse> {
    const database = await this.getDatabase(location.databaseName, location.params);
    const transaction = database.transaction([location.objectStoreName], 'readwrite');
    const store = transaction.objectStore(location.objectStoreName);
    return await new Promise((resolve, reject) => {
      const isInlineKey = (store.keyPath?.length ?? 0) > 0;
      const request = store.put(resource, isInlineKey ? undefined : location.key);
      request.onsuccess = (event) => {
        resolve(resource as any);
      };
      request.onerror = () => {
        console.log(
          `Cannot update resource ${location.key} in ${location.databaseName}/${location.objectStoreName}`
        );
        reject(request.error);
      };
    });
  }

  deleteDatabase(name: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const db = await this.getDatabase(name);
      db.close();
      this.databases.splice(this.databases.indexOf(db), 1);

      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => {
        console.log(`The IndexDB database '${name}' has been deleted`);
        resolve();
      };
      request.onerror = () => {
        console.log(`Cannot delete the IndexDB database '${name}'`, request.error);
        reject(request.error);
      };
      request.onblocked = () => {
        console.log(
          `The deleation of the IndexDB database '${name}' was blocked by another operation`
        );
        reject(request.error);
      };
    });
  }

  private getDatabase(name: string, params?: Dictionary<any>): Promise<IDBDatabase> {
    const interpolatedName = interpolate(name, params ?? {});
    const existingDb = this.databases.find((o) => o.name === interpolatedName);
    if (existingDb)
      return new Promise((resolve, reject) =>
        !!existingDb ? resolve(existingDb) : reject()
      );

    let config: any;
    try {
      config = this.injector.get(SOFTLINE_CONFIG_INDEXED_DB).find((o) => o.name === name);
      if (!config) throw new Error(`No indexedDB configuration found for '${name}'`);
    } catch (e) {
      console.log(e);
      throw e;
    }

    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(interpolatedName, config.version);

      request.onsuccess = (result) => {
        const database = request.result;
        this.databases.push(database);
        resolve(database);
      };
      request.onerror = () => {
        console.log(`Cannot open Database '${name}'`);
        reject(`Cannot open Database '${name}'`);
      };
      request.onblocked = () => {
        alert(
          `Cannot upgrade Database '${name}'. Please close all instances of the app and try again`
        );
        reject(
          `Cannot upgrade Database '${name}'. Please close all instances of the app and try again`
        );
      };
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const database = request.result;
        const transaction = (event.target as any).transaction;

        if (event.oldVersion < 1) {
          for (const storeConfig of config.objectStoreConfigs) {
            const store = database.createObjectStore(
              storeConfig.name,
              storeConfig.parameters
            );
            for (const index of storeConfig.schema)
              store.createIndex(index.name, index.keypath, index.options);
          }
        }
        if (config.migrationCallbacks && request.transaction) {
          for (
            let i = event.oldVersion;
            i < (event.newVersion ?? event.oldVersion);
            i++
          ) {
            if (config.migrationCallbacks[i])
              config.migrationCallbacks[i](
                (event.target as any).result,
                request.transaction
              );
          }
        }
        transaction.oncomplete = () => resolve(database);
      };
    });
  }

  private createCopyWithKey<T>(resource: T, key: string, keyPath: string | string[]): T {
    const returnValue = { ...resource } as any;
    let currentValue = returnValue;
    keyPath = typeof keyPath === 'string' ? [keyPath] : keyPath;
    for (let i = 0; i < keyPath.length; i++) {
      if (i === keyPath.length - 1) currentValue[keyPath[1]] = key;
      else {
        currentValue[keyPath[1]] = {};
        currentValue = currentValue[keyPath[1]];
      }
    }
    return returnValue;
  }
}
